import FullscreenBackground from "@/features/Common/components/FullscreenBackground";
import IconLoader from "@/features/Common/components/IconLoader";
import { trackEvent, trackPageViewV2 } from "@/services/analytics-adapter";
import { overrideDefaultLinkStyling } from "@/theme";
import { convertPxToRem } from "@/utils";
import { Button, Stack, Typography } from "@mui/material";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

interface DoctorBookingConfirmationProps {
  onContinue: () => void;
  onGoBackHome: () => void;
}
const EMERGENCY_PHONE_NUMBER_DIALING_CODE_ZA = "24";
const EMERGENCY_PHONE_NUMBER_ZA = "084 124";
const EMERGENCY_PROVIDER_ZA = "ER24";

export default function DoctorBookingConfirmation({
  onContinue,
  onGoBackHome,
}: DoctorBookingConfirmationProps) {
  const { t } = useTranslation();

  function onUrgentCareClick() {
    trackEvent({
      event: "action.urgentCarePhoneNumber",
      source: "booking confirmation screen",
    });
  }

  useEffect(() => {
    trackPageViewV2({
      pageName: "Medical assistance:Doctor booking confirmation",
      pageSubSection1: "Medical assistance",
      pageSubSection2: "Medical assistance:Doctor booking confirmation",
      pageCategory: "Medical assistance",
    });
  }, []);

  return (
    <>
      <Stack
        justifyContent="flex-end"
        color="primary.contrastText"
        spacing={6}
        sx={{
          height: "100%",
        }}
      >
        <Stack p={2} spacing={4}>
          <IconLoader
            icon="DoctorBookingConfirmationIcon"
            color="primary"
            sx={{
              fontSize: convertPxToRem(150),
              alignSelf: "center",
            }}
          />

          <Stack textAlign="center" spacing={4}>
            <Typography variant="h1" color="inherit">
              {t("DoctorBookingConfirmation.title")}
            </Typography>

            <Typography variant="h3" color="inherit">
              {t("DoctorBookingConfirmation.subTitle")}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button
              onClick={onGoBackHome}
              color="info"
              variant="outlined"
              fullWidth
            >
              {t("common.noThankYou")}
            </Button>
            <Button onClick={onContinue} color="info" fullWidth>
              {t("common.yesPlease")}
            </Button>
          </Stack>
        </Stack>
        <Stack
          spacing={1}
          p={3}
          bgcolor="background.paper"
          width="100%"
          alignItems="center"
          onClick={onUrgentCareClick}
          component="a"
          href={`tel:${EMERGENCY_PHONE_NUMBER_ZA}`}
          target="_self"
          sx={{
            ...overrideDefaultLinkStyling,
            cursor: "pointer",
          }}
        >
          <Stack direction="row" gap={1} alignItems="center">
            <Typography variant="h4" color="neutral.dark">
              {t("MedicalAssistance.urgentCare.title")}
            </Typography>

            <Stack
              direction="row"
              alignSelf="center"
              alignItems="center"
              color="primary.main"
            >
              <IconLoader icon="PhoneIcon" />
              <Typography variant="h4" ml={0.5} color="inherit">
                {t("MedicalAssistance.urgentCare.phoneText", {
                  emergencyProvider: EMERGENCY_PROVIDER_ZA,
                })}
              </Typography>
            </Stack>
          </Stack>

          <Typography color="neutral.light" variant="body2">
            {t("MedicalAssistance.urgentCare.subText", {
              phoneNumber: `${EMERGENCY_PHONE_NUMBER_DIALING_CODE_ZA} ${EMERGENCY_PHONE_NUMBER_ZA}`,
            })}
          </Typography>
        </Stack>
      </Stack>

      <FullscreenBackground color="primary.main" />
    </>
  );
}
